import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Target, X } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    listPixelsForCampaign,
    listPixelsForDomain,
    listPixelsPaged,
    attachPixelToCampaign,
    detachPixelFromCampaign,
    attachPixelToDomain,
    detachPixelFromDomain,
} from "@/api/pixels";
import { providerSpec, type PixelDto } from "@/types/pixel";
import { apiErrorMessage } from "@/lib/api";

interface Props {
    /** Either campaignId or domainId must be set; not both. */
    campaignId?: number;
    domainId?: number;
}

/**
 * Reusable card showing pixels currently attached to a campaign or domain,
 * plus an attach picker. Lists every pixel in the system in the picker so
 * the operator can mix providers without leaving the page.
 */
export function PixelsCard({ campaignId, domainId }: Props): React.ReactNode {
    // Every hook below runs unconditionally (rules of hooks). The "nothing to
    // show" guard lives AFTER the hooks; queries are gated with `enabled` so
    // they don't fire when there's no target. Mirrors banners-panel.tsx — an
    // early return before these hooks caused a "rendered fewer hooks" crash.
    const hasTarget = campaignId != null || domainId != null;
    const scope: "campaign" | "domain" = campaignId != null ? "campaign" : "domain";
    const queryClient = useQueryClient();

    const attachedQuery = useQuery({
        queryKey: ["pixels-attached", scope, campaignId ?? domainId],
        queryFn: () =>
            campaignId != null ? listPixelsForCampaign(campaignId) : listPixelsForDomain(domainId!),
        enabled: hasTarget,
    });
    const allPixelsQuery = useQuery({
        queryKey: ["pixels-all-for-attach"],
        queryFn: () => listPixelsPaged({ size: 200, sort: "name,asc" }),
        enabled: hasTarget,
    });

    const attached = attachedQuery.data ?? [];
    const attachedIds = new Set(attached.map((p) => p.id));
    const candidates = (allPixelsQuery.data?.content ?? []).filter((p) => !attachedIds.has(p.id));

    const attachMut = useMutation({
        mutationFn: (pixelId: number) =>
            campaignId != null
                ? attachPixelToCampaign(campaignId, pixelId)
                : attachPixelToDomain(domainId!, pixelId),
        onSuccess: () => {
            toast.success("Pixel attached");
            queryClient.invalidateQueries({ queryKey: ["pixels-attached", scope] });
            queryClient.invalidateQueries({ queryKey: ["pixels"] });
        },
        onError: (e) => toast.error(apiErrorMessage(e)),
    });
    const detachMut = useMutation({
        mutationFn: (pixelId: number) =>
            campaignId != null
                ? detachPixelFromCampaign(campaignId, pixelId)
                : detachPixelFromDomain(domainId!, pixelId),
        onSuccess: () => {
            toast.success("Pixel detached");
            queryClient.invalidateQueries({ queryKey: ["pixels-attached", scope] });
            queryClient.invalidateQueries({ queryKey: ["pixels"] });
        },
        onError: (e) => toast.error(apiErrorMessage(e)),
    });

    const [pickerValue, setPickerValue] = React.useState<string>("");

    if (!hasTarget) return null;

    return (
        <Card data-testid="pixels-card">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium">
                        <Target className="h-4 w-4" />
                        Pixels
                    </div>
                    <Link to="/pixels" className="text-xs text-muted-foreground hover:underline">
                        Manage all →
                    </Link>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {attached.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                        No pixels attached.{" "}
                        {scope === "campaign"
                            ? "Pick one below to track this campaign's traffic."
                            : "These run sitewide on the domain when no campaign matches."}
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-2" data-testid="attached-pixels">
                        {attached.map((p) => (
                            <PixelChip
                                key={p.id}
                                pixel={p}
                                onDetach={() => detachMut.mutate(p.id)}
                            />
                        ))}
                    </div>
                )}

                {candidates.length > 0 && (
                    <div className="flex items-center gap-2">
                        <Select
                            value={pickerValue}
                            onValueChange={(v) => setPickerValue(v)}
                        >
                            <SelectTrigger className="flex-1" data-testid="pixel-attach-select">
                                <SelectValue placeholder="Attach a pixel…" />
                            </SelectTrigger>
                            <SelectContent>
                                {candidates.map((p) => (
                                    <SelectItem key={p.id} value={p.id.toString()}>
                                        {p.name} · {providerSpec(p.provider)?.label ?? p.provider}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            size="sm"
                            disabled={!pickerValue || attachMut.isPending}
                            onClick={() => {
                                attachMut.mutate(Number(pickerValue), {
                                    onSuccess: () => setPickerValue(""),
                                });
                            }}
                            data-testid="pixel-attach-button"
                        >
                            <Plus className="h-4 w-4" />
                            Attach
                        </Button>
                    </div>
                )}

                {allPixelsQuery.data && allPixelsQuery.data.content.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                        No pixels created yet.{" "}
                        <Link to="/pixels" className="underline hover:text-foreground">
                            Create one
                        </Link>{" "}
                        to start.
                    </p>
                )}
            </CardContent>
        </Card>
    );
}

function PixelChip({ pixel, onDetach }: { pixel: PixelDto; onDetach: () => void }): React.ReactNode {
    const label = providerSpec(pixel.provider)?.label ?? pixel.provider;
    return (
        <div
            className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs"
            data-testid={`pixel-chip-${pixel.id}`}
        >
            <Badge variant="muted" className="px-1.5 py-0 text-[10px]">
                {label}
            </Badge>
            <span className="font-medium">{pixel.name}</span>
            {pixel.externalId && (
                <span className="font-mono text-[10px] text-muted-foreground">{pixel.externalId}</span>
            )}
            {!pixel.enabled && <Badge variant="warning">disabled</Badge>}
            <button
                onClick={onDetach}
                className="text-muted-foreground hover:text-destructive"
                aria-label={`Detach ${pixel.name}`}
                data-testid={`pixel-detach-${pixel.id}`}
            >
                <X className="h-3 w-3" />
            </button>
        </div>
    );
}
